import React, {
  FC,
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import FilterTag, { IFilterTagData } from "./FilterTag";
import useWatchState from "../../../hooks/useWatchState";
import HiddenPortal from "../../Utility/ResponsiveContainer/HiddenPortal";
import { getInnerDimensions } from "../../Utility/ResponsiveContainer/elementDimensions";
import { useEventListener } from "../../../hooks";
import { outerHeight, getDefaultScrollConfig } from "../../../helpers";

export interface IFilterTagsList {
  /**
   * Filters list data
   */
  filterTags: IFilterTagData[];

  /**
   * Translation data object
   */
  labels?: {
    /** Text of 'clear all' button */
    clearAll?: string;
  };

  /**
   * Callback function for when a tag is removed
   */
  onChange?(tag: IFilterTagData): void;

  /**
   * Callback function for when clear all is clicked
   */
  onClearAll?(): void;

  /** Fires when elements finished entering or exiting */
  onAnimationEnded?(): void;
}

const listenerConfig = getDefaultScrollConfig();

const FilterTagsList: FC<IFilterTagsList> = ({
  filterTags,
  labels,
  onChange,
  onClearAll,
  onAnimationEnded,
}) => {
  /** Refs */
  const displayContainer = useRef<HTMLDivElement>(null);
  const hiddenContainer = useRef<HTMLDivElement>(null);

  /** Create single ref for clear all */
  const clearAllRef = useMemo(() => React.createRef<HTMLButtonElement>(), []);

  const [tags, setTags] = useWatchState<Array<IFilterTagData>>({
    value: filterTags,
  });

  /** Add ref to each filterTag for Transition group */
  const filterRefs: RefObject<HTMLButtonElement>[] = useMemo(
    () => tags.map(() => React.createRef<HTMLButtonElement>()),
    [tags],
  );

  const removeTag = useCallback(
    (event: MouseEvent, tag: IFilterTagData) => {
      setTags(tags.filter((item) => item.id !== tag.id));
      onChange && onChange(tag);

      event.preventDefault();
      event.stopPropagation();
    },
    [onChange, setTags, tags],
  );

  const handleClearAll = useCallback(() => {
    setTags([]);
    onClearAll && onClearAll();
  }, [onClearAll, setTags]);

  const tagsWithClear: Array<IFilterTagData | "last"> = useMemo(
    () => (tags.length > 1 ? tags.concat("last" as any) : tags),
    [tags],
  );

  const handleAnimationEnd = useCallback(() => {
    onAnimationEnded && onAnimationEnded();
  }, [onAnimationEnded]);

  const mockedNonAnimList = useMemo(
    () =>
      tagsWithClear?.map((tag, index) =>
        tag === "last" ? (
          <FilterTag
            data={{ id: "clearAll", text: labels?.clearAll, category: "" }}
            isClearAll
            onClick={handleClearAll}
          />
        ) : (
          <FilterTag ref={filterRefs[index]} data={tag} onClick={removeTag} />
        ),
      ),
    [filterRefs, handleClearAll, labels.clearAll, removeTag, tagsWithClear],
  );

  const [visibleElWidth, setVisibleElWidth] = useState<string>("auto");
  const [visibleElHeight, setVisibleElHeight] = useState<string>("auto");

  const calcWidth = useCallback(() => {
    const displayDims = getInnerDimensions(displayContainer.current);
    const width = displayDims.width ? `${String(displayDims.width)}px` : "none";
    setVisibleElWidth(width);
  }, []);
  useCallback(calcWidth, [calcWidth, tagsWithClear]);

  useEventListener("resize", calcWidth, listenerConfig);
  useEventListener("scroll", calcWidth, listenerConfig);

  const calcMaxHeight = useCallback(() => {
    if (hiddenContainer.current) {
      const height = outerHeight(hiddenContainer.current);
      const maxH = `${String(height)}px`;
      setVisibleElHeight(maxH);
    }
  }, []);

  useEffect(calcMaxHeight, [calcMaxHeight, tagsWithClear]);
  useEventListener("resize", calcMaxHeight, listenerConfig);
  useEventListener("scroll", calcMaxHeight, listenerConfig);

  const hiddenElStyles = useMemo(
    () => ({ maxWidth: visibleElWidth }),
    [visibleElWidth],
  );

  const filterStyles = useMemo(
    () => ({ height: visibleElHeight }),
    [visibleElHeight],
  );

  return (
    <>
      {/* Container for pre-rendering of children dimensions */}
      <HiddenPortal ref={hiddenContainer} style={hiddenElStyles}>
        {mockedNonAnimList}
      </HiddenPortal>

      <div
        className="filter-tags"
        ref={displayContainer}
        style={filterStyles}
        onAnimationEnd={onAnimationEnded}
        onTransitionEnd={onAnimationEnded}
      >
        <TransitionGroup component={null}>
          {tagsWithClear?.map((tag, index) =>
            tag === "last" ? (
              <CSSTransition
                key="last"
                nodeRef={clearAllRef}
                appear
                classNames="uds-appear-fade"
                timeout={200}
                mountOnEnter
                unmountOnExit
                onExited={handleAnimationEnd}
                onEntered={handleAnimationEnd}
              >
                <FilterTag
                  data={{
                    id: "clearAll",
                    text: labels?.clearAll,
                    category: "",
                  }}
                  isClearAll
                  onClick={handleClearAll}
                />
              </CSSTransition>
            ) : (
              <CSSTransition
                key={tag.id}
                nodeRef={filterRefs[index]}
                appear
                classNames="uds-appear-fade"
                timeout={200}
                mountOnEnter
                unmountOnExit
                onExited={handleAnimationEnd}
                onEntered={handleAnimationEnd}
              >
                <FilterTag
                  ref={filterRefs[index]}
                  data={tag}
                  onClick={removeTag}
                />
              </CSSTransition>
            ),
          )}
        </TransitionGroup>
      </div>
    </>
  );
};

export default FilterTagsList;
